"use client"

import { useMemo } from "react"
import { format } from "date-fns"
import { stageHex } from "@/lib/stage-colors"
import { Calendar } from "lucide-react"
import type { CandidateTimelineItemV1 } from "openapi/api/model"

type UiTimelineEvent =
  | { kind: "placement_created"; at: Date }
  | { kind: "start_date"; at: Date }
  | { kind: "end_date"; at: Date }
  | { kind: "activity"; at: Date; item: CandidateTimelineItemV1 }

export interface PlacementTimelineCardProps {
  createdAt?: Date | string | null | undefined
  startDate?: Date | string | null | undefined
  endDate?: Date | string | null | undefined
  items?: CandidateTimelineItemV1[] | null
  title?: string
  /**
   * If true, only show activities if BOTH startDate and endDate exist,
   * and the activity timestamp is within that range (inclusive).
   *
   * If false (default), missing bounds simply mean "no bound" on that side.
   */
  strictBounds?: boolean
  filterToPlacementDates?: boolean

}

function safeToDate(value: Date | string | null | undefined): Date | null {
    if (!value) return null
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  
    // If backend sent a date-only string (YYYY-MM-DD), treat it as LOCAL midnight
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1])
      const monthIndex = Number(dateOnlyMatch[2]) - 1 // 0-based
      const day = Number(dateOnlyMatch[3])
      const d = new Date(year, monthIndex, day) // local time
      return Number.isNaN(d.getTime()) ? null : d
    }
  
    // Otherwise, ISO timestamps with timezone offset are safe
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  

function isWithin(at: Date, start: Date | null, end: Date | null): boolean {
  if (start && at < start) return false
  if (end && at > end) return false
  return true
}

/**
 * An event's colour, from the app's one ramp.
 *
 * These were raw Tailwind palette steps — `bg-green-500`, `bg-blue-500`,
 * `bg-pink-500` — picked against a dark ground. The stage ramp already holds
 * the same hues and restates each of them darker for light mode, so the
 * timeline stays legible in both themes without a second set of colours.
 * `calendar_event` had pink, which the ramp has no step for; it takes
 * `rejected` as the nearest warm, distinct hue.
 */
function getDotHex(ev: UiTimelineEvent): string {
  if (ev.kind === "placement_created") return stageHex("hired")
  if (ev.kind === "start_date") return stageHex("shortlist")
  if (ev.kind === "end_date") return stageHex("longlist")

  switch (ev.item.type) {
    case "outreach_sent":
      return stageHex("outbound")
    case "comment":
      return stageHex("submitted")
    case "task":
      return stageHex("interviewing")
    case "stage_transition":
      return stageHex("screening")
    case "calendar_event":
      return stageHex("rejected")
    default:
      return stageHex("longlist")
  }
}

function getActivityTitle(item: CandidateTimelineItemV1): string {
  const t = item.title?.trim()
  if (t) return t

  switch (item.type) {
    case "outreach_sent":
      return "Outreach sent"
    case "comment":
      return "Comment"
    case "task":
      return "Task"
    case "stage_transition":
      return "Stage transition"
    case "calendar_event":
      return "Calendar event"
    default:
      return "Activity"
  }
}

function getActivitySubtitle(item: CandidateTimelineItemV1): string | null {
  const s = item.subtitle?.trim()
  if (s) return s

  const d = item.details

  if (item.type === "stage_transition" && (d.from_stage || d.to_stage)) {
    // No from-stage is the candidate arriving on the job.
    return `${d.from_stage ?? "added"} → ${d.to_stage ?? "—"}`
  }

  if (item.type === "task" && (d.status || d.priority)) {
    const parts = [d.status, d.priority].filter(Boolean) as string[]
    return parts.length ? parts.join(" • ") : null
  }

  if (item.type === "calendar_event" && (d.event_type || d.location)) {
    const parts = [d.event_type, d.location].filter(Boolean) as string[]
    return parts.length ? parts.join(" • ") : null
  }

  const text = d.body ?? d.description ?? d.notes ?? null
  if (!text) return null

  const trimmed = text.trim()
  if (!trimmed) return null

  return trimmed.length > 90 ? `${trimmed.slice(0, 90)}…` : trimmed
}

function buildEvents(args: {
    createdAt: Date | null
    startDate: Date | null
    endDate: Date | null
    items: CandidateTimelineItemV1[]
    strictBounds: boolean
    filterToPlacementDates?: boolean
  }): UiTimelineEvent[] {
    const { createdAt, startDate, endDate, items, strictBounds, filterToPlacementDates } = args
  
    const events: UiTimelineEvent[] = []
    if (createdAt) events.push({ kind: "placement_created", at: createdAt })
    if (startDate) events.push({ kind: "start_date", at: startDate })
  
    for (const item of items) {
      const at = safeToDate(item.occurred_at)
      if (!at) continue
  
      if (filterToPlacementDates) {
        if (strictBounds) {
          if (!startDate || !endDate) continue
          if (at < startDate || at > endDate) continue
        } else {
          if (!isWithin(at, startDate, endDate)) continue
        }
      }
  
      events.push({ kind: "activity", at, item })
    }
  
    if (endDate) events.push({ kind: "end_date", at: endDate })
  
    events.sort((a, b) => {
      const rank = (ev: UiTimelineEvent) => {
        if (ev.kind === "start_date") return 0      // ALWAYS top
        if (ev.kind === "placement_created") return 1
        if (ev.kind === "activity") return 2
        if (ev.kind === "end_date") return 3        // ALWAYS bottom
        return 2
      }
    
      const ra = rank(a)
      const rb = rank(b)
      if (ra !== rb) return ra - rb
    
      // within same group, sort by time
      const ta = a.at.getTime()
      const tb = b.at.getTime()
      if (ta !== tb) return ta - tb
    
      // stable-ish tie breaker so React keys don't shuffle weirdly
      const ka = a.kind === "activity" ? a.item.id : a.kind
      const kb = b.kind === "activity" ? b.item.id : b.kind
      return ka.localeCompare(kb)
    })
    
      
    return events
  }
  
export function PlacementTimelineCard({
  createdAt,
  startDate,
  endDate,
  items,
  title = "Timeline",
  strictBounds = false,
  filterToPlacementDates
}: PlacementTimelineCardProps) {
  const created = useMemo(() => safeToDate(createdAt), [createdAt])
  const start = useMemo(() => safeToDate(startDate), [startDate])
  const end = useMemo(() => safeToDate(endDate), [endDate])

  const safeItems = items ?? []

  const events = useMemo(
    () =>
    buildEvents({
        createdAt: created,
        startDate: start,
        endDate: end,
        items: safeItems,
        strictBounds,
        filterToPlacementDates,
      }),      
    [created, start, end, safeItems, strictBounds],
  )

  return (
    <section className="mt-6 border-t pt-5">
      <h3 className="mb-3 flex items-center gap-2 text-[12px] font-medium">
        <Calendar className="size-3.5 text-muted-foreground" />
        {title}
      </h3>

      <div>
        {events.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">No timeline events.</p>
        ) : (
          <div className="space-y-3">
            {events.map((ev, idx) => {
              if (ev.kind === "placement_created") {
                return (
                  <div key={`pc-${idx}`} className="flex items-center gap-3">
                    <span aria-hidden className="inline-block size-2 flex-none rounded-full" style={{ backgroundColor: getDotHex(ev) }} />
                    <div className="flex-1">
                      <div className="text-[12.5px] font-medium leading-[1.3]">Placement Created</div>
                      <div className="font-mono text-[11px] leading-[1.4] text-muted-foreground">
                        {format(ev.at, "MMM dd, yyyy 'at' h:mm a")}
                      </div>
                    </div>
                  </div>
                )
              }

              if (ev.kind === "start_date") {
                return (
                  <div key={`sd-${idx}`} className="flex items-center gap-3">
                    <span aria-hidden className="inline-block size-2 flex-none rounded-full" style={{ backgroundColor: getDotHex(ev) }} />
                    <div className="flex-1">
                      <div className="text-[12.5px] font-medium leading-[1.3]">Start Date</div>
                      <div className="font-mono text-[11px] leading-[1.4] text-muted-foreground">{format(ev.at, "MMM dd, yyyy")}</div>
                    </div>
                  </div>
                )
              }

              if (ev.kind === "end_date") {
                return (
                  <div key={`ed-${idx}`} className="flex items-center gap-3">
                    <span aria-hidden className="inline-block size-2 flex-none rounded-full" style={{ backgroundColor: getDotHex(ev) }} />
                    <div className="flex-1">
                      <div className="text-[12.5px] font-medium leading-[1.3]">End Date</div>
                      <div className="font-mono text-[11px] leading-[1.4] text-muted-foreground">{format(ev.at, "MMM dd, yyyy")}</div>
                    </div>
                  </div>
                )
              }

              // activity
              const t = getActivityTitle(ev.item)
              const sub = getActivitySubtitle(ev.item)

              return (
                <div key={ev.item.id} className="flex items-start gap-3">
                  <span aria-hidden className="mt-1.5 inline-block size-2 flex-none rounded-full" style={{ backgroundColor: getDotHex(ev) }} />
                  <div className="flex-1">
                    <div className="text-[12.5px] font-medium leading-[1.3]">{t}</div>
                    {sub ? <div className="font-mono text-[11px] leading-[1.4] text-muted-foreground">{sub}</div> : null}
                    <div className="text-xs text-muted-foreground mt-1">{format(ev.at, "MMM dd, yyyy 'at' h:mm a")}</div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}
